import { useTranslation } from 'react-i18next';
import { CHANNEL_COLORS } from './colors';
import { PanelHead, ServiceMark } from './panel';
import { metricText, type Fmt } from './format';
import type { PeriodReport } from './use-report';

/** Marketing view: cost per qualified lead and per meeting for the paid channels, and contact quality per source. */
export function ChannelQuality({ report, fmt }: { report: PeriodReport; fmt: Fmt }) {
  const { t } = useTranslation(['performance', 'common']);
  const paid = report.channels.filter((c) => c.spend && c.spend.costCzk > 0);
  const max = Math.max(
    1,
    ...paid.flatMap((c) => [c.economics.costPerLevel[1]?.value ?? 0, c.economics.costPerLevel[2]?.value ?? 0]),
  );
  return (
    <div className="pa-two-col">
      <article className="pa-panel">
        <PanelHead title={t('dashboard.quality.title')} subtitle={t('dashboard.quality.subtitle')} />
        <div className="pa-channel-bars">
          {paid.map((c) => (
            <div key={c.channel} className="pa-channel-group">
              <h3>
                <ServiceMark channel={c.channel} />
                <span>{t(`channel.${c.channel}`)}</span>
              </h3>
              {([1, 2] as const).map((level) => {
                const m = c.economics.costPerLevel[level];
                const width = m?.value != null ? (m.value / max) * 100 : 0;
                return (
                  <div key={level}>
                    <span>{level === 1 ? t('dashboard.quality.cpql') : t('dashboard.quality.meeting')}</span>
                    <div className="pa-channel-track" style={{ ['--bar-width' as string]: `${width}%` }}>
                      <i
                        aria-hidden="true"
                        style={{
                          ['--series-color' as string]: CHANNEL_COLORS[c.channel] ?? '#00a879',
                          width: `${width}%`,
                        }}
                      />
                      <strong className={width > 65 ? 'pa-channel-value-end' : ''}>
                        {m ? metricText(m, (v) => fmt.czk(v)) : '—'}
                      </strong>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
          {paid.length === 0 && <p className="pa-empty">{t('dashboard.quality.empty')}</p>}
        </div>
        <p className="pa-footnote">{t('dashboard.quality.footnote')}</p>
      </article>
      <article className="pa-panel">
        <PanelHead title={t('dashboard.quality.contactsTitle')} subtitle={t('dashboard.quality.contactsSubtitle')} />
        <div className="pa-quality">
          {report.channels
            .filter((c) => c.metrics.leads > 0)
            .map((c) => {
              const q = (c.metrics.reached[1] ?? 0) / c.metrics.leads;
              const nc = c.metrics.noContact / c.metrics.leads;
              return (
                <div key={c.channel} className="pa-quality-row">
                  <div>
                    <strong className="pa-quality-source">
                      <ServiceMark channel={c.channel} />
                      <span>{t(`channel.${c.channel}`)}</span>
                    </strong>
                    <span>
                      {t('dashboard.quality.leads', { count: c.metrics.leads, formatted: fmt.int(c.metrics.leads) })}
                    </span>
                  </div>
                  <div className="pa-quality-track">
                    <i
                      className={nc ? '' : 'pa-quality-last'}
                      style={{
                        ['--series-color' as string]: CHANNEL_COLORS[c.channel] ?? '#00a879',
                        width: `${q * 100}%`,
                      }}
                    />
                    <em style={{ width: `${nc * 100}%` }} />
                  </div>
                  <p>
                    <span>
                      <b>{fmt.pct(q)}</b> {t('dashboard.quality.qualifiedShare')}
                    </span>
                    <span>
                      <b>{fmt.pct(nc)}</b> {t('dashboard.quality.noContactShare')}
                    </span>
                  </p>
                </div>
              );
            })}
        </div>
        <p className="pa-footnote">{t('dashboard.quality.contactsFootnote')}</p>
      </article>
    </div>
  );
}
